<script setup lang="ts">
  import {
    CasesService,
    OnboardingSessionsService,
    type ClientCase,
    type OnboardingPackageSessionDetail,
    type ClientOrganisation
  } from '@/types/generated'
  import { computed, onMounted, provide, ref, watch } from 'vue'
  import { LoaderCircle, ChevronsRight } from 'lucide-vue-next'
  import client404 from '@/components/404/404.vue'
  import Sidebar from '@/components/clientPortal/Sidebar.vue'
  import { useRoute } from 'vue-router'
  import { provideQuestionnaire } from '@/composables/useQuestionnaire'

  const route = useRoute()
  const urlParams = new URLSearchParams(window.location.search)
  const accessToken = ref(urlParams.get('access_token'))
  const sessionToken = ref(urlParams.get('session_token'))
  provide('accessToken', accessToken)
  provide('sessionToken', sessionToken)
  const caseRecord = ref<ClientCase | null>(null)
  const onboardingSession = ref<OnboardingPackageSessionDetail | null>(null)
  provide('caseRecord', caseRecord)
  provide('onboardingSession', onboardingSession)
  const org = ref<ClientOrganisation | null>(null)
  provide('org', org)

  // Provide questionnaire data for sidebar (will be set by child components)
  const questionnaire = ref<any | null>(null)
  provide('questionnaire', questionnaire)

  // Provide activeField for sidebar navigation
  const activeField = ref('')
  provide('activeField', activeField)

  provideQuestionnaire()

  const isDataLoaded = ref(false)
  const hasError = ref(false)

  const sidebarVisible = ref(true)
  provide('sidebarVisible', sidebarVisible)

  const isExternalRoute = computed(() => {
    return route.path.includes('/external/')
  })

  const isPreviewRoute = computed(() => {
    return route.path.includes('/preview/')
  })

  const isPdfTemplateRoute = computed(() => {
    return route.path.includes('/pdf-templates/')
  })

  const isCompletedQuestionnaireRoute = computed(() => {
    return route.path.includes('/external/questionnaire/completed')
  })

  const isQuestionnaireClosed = computed(() => {
    return questionnaire.value?.state === 'CLOSED' || questionnaire.value?.closed === true
  })

  const isPdfFormSubmissionRoute = computed(() => {
    return route.path.includes('/pdf-form-submissions/')
  })

  const shouldShowSidebar = computed(() => {
    return (
      !isPdfTemplateRoute.value &&
      !isCompletedQuestionnaireRoute.value &&
      !isQuestionnaireClosed.value &&
      !isPdfFormSubmissionRoute.value
    )
  })

  const toggleSidebar = () => {
    sidebarVisible.value = !sidebarVisible.value
  }

  const loadCaseData = async () => {
    // Handle PDF form submission routes - no need to load case data
    // The PDFFormView component loads the submission directly
    if (isPdfFormSubmissionRoute.value) {
      isDataLoaded.value = true
      return
    }

    if (isExternalRoute.value || isPreviewRoute.value) {
      const orgToken = urlParams.get('org_token')

      // If we have an org_token, load organization data directly
      if (orgToken) {
        try {
          const response = await fetch(`/api/v3/organisations/by-org-token/?org_token=${orgToken}`)
          if (response.ok) {
            const orgData = await response.json()
            org.value = orgData as ClientOrganisation
          }
        } catch (error) {
          console.error('Error loading organisation data for standalone questionnaire:', error)
        }
      }

      if (accessToken.value && !orgToken && !isExternalRoute.value) {
        try {
          const response = await CasesService.casesByAccessTokenRetrieve({
            accessToken: accessToken.value
          })
          caseRecord.value = response as ClientCase

          if (caseRecord.value) {
            org.value = caseRecord.value.org as ClientOrganisation
          }
        } catch (error) {
          console.error('Error loading organisation data for external route:', error)
          // Don't set hasError to true for external routes, just continue without org data
        }
      }
      isDataLoaded.value = true
      return
    }

    // Handle onboarding session via session_token
    if (sessionToken.value) {
      try {
        const sessionData = await OnboardingSessionsService.onboardingSessionsByTokenRetrieve({
          sessionToken: sessionToken.value
        })
        onboardingSession.value = sessionData as OnboardingPackageSessionDetail

        // Session may or may not have an associated case
        if (onboardingSession.value?.case) {
          caseRecord.value = onboardingSession.value.case as ClientCase
          org.value = onboardingSession.value.case.org as ClientOrganisation
        }

        isDataLoaded.value = true
      } catch (error) {
        console.error('Error loading onboarding session data:', error)
        hasError.value = true
        isDataLoaded.value = true
      }
      return
    }

    try {
      const response = await CasesService.casesByAccessTokenRetrieve({
        accessToken: accessToken.value || ''
      })
      caseRecord.value = response as ClientCase

      if (caseRecord.value) {
        org.value = caseRecord.value.org as ClientOrganisation
      }

      isDataLoaded.value = true
    } catch (error) {
      console.error('Error loading client portal data:', error)
      hasError.value = true
      isDataLoaded.value = true
    }
  }

  const refreshData = async () => {
    isDataLoaded.value = false
    hasError.value = false
    await loadCaseData()
  }
  provide('refreshData', refreshData)

  onMounted(async () => {
    await loadCaseData()
  })
</script>

<template>
  <div
    v-if="isDataLoaded && !hasError"
    data-cy="ClientPortalBase"
  >
    <div
      v-if="
        isExternalRoute ||
        isPreviewRoute ||
        isPdfFormSubmissionRoute ||
        caseRecord ||
        onboardingSession
      "
      class="h-screen"
    >
      <div
        v-if="shouldShowSidebar"
        class="flex h-full"
      >
        <!-- Desktop sidebar (flex layout) -->
        <div
          class="relative hidden transition-all duration-300 ease-in-out md:block"
          :class="sidebarVisible ? 'w-[250px]' : 'w-16'"
        >
          <Sidebar
            class="absolute inset-0 transition-all duration-300 ease-in-out"
            :class="
              sidebarVisible
                ? 'translate-x-0 opacity-100'
                : 'pointer-events-none -translate-x-full opacity-0'
            "
            @toggle="toggleSidebar"
          />
          <div
            class="absolute inset-0 flex flex-col items-center justify-start border-r border-gray-200 bg-white pt-4
              transition-all duration-300 ease-in-out"
            :class="
              !sidebarVisible
                ? 'translate-x-0 opacity-100'
                : 'pointer-events-none translate-x-full opacity-0'
            "
          >
            <button
              data-cy="ClientPortalBaseSidebarOpenButton"
              class="rounded-md p-2 transition-colors hover:bg-gray-100"
              @click="toggleSidebar"
            >
              <ChevronsRight class="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>

        <!-- Mobile sidebar (overlay) -->
        <div
          class="fixed inset-0 z-50 transition-all duration-300 ease-in-out md:hidden"
          :class="sidebarVisible ? 'pointer-events-auto' : 'pointer-events-none'"
        >
          <div
            class="absolute inset-0 bg-black transition-opacity duration-300"
            :class="sidebarVisible ? 'opacity-50' : 'opacity-0'"
            @click="toggleSidebar"
          ></div>

          <div
            data-cy="ClientPortalBaseMobileSidebar"
            class="absolute left-0 top-0 h-full w-[280px] bg-white shadow-lg transition-transform duration-300
              ease-in-out"
            :class="sidebarVisible ? 'translate-x-0' : '-translate-x-full'"
          >
            <Sidebar @toggle="toggleSidebar" />
          </div>
        </div>

        <!-- Mobile header -->
        <div class="fixed left-0 right-0 top-0 z-40 border-b border-gray-200 bg-white md:hidden">
          <div class="flex h-14 items-center justify-between px-6">
            <button
              data-cy="ClientPortalBaseMobileMenuButton"
              class="rounded-md p-2 transition-colors hover:bg-gray-100"
              @click="toggleSidebar"
            >
              <svg
                class="h-6 w-6 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 6h16M4 12h16M4 18h16"
                ></path>
              </svg>
            </button>

            <div class="flex flex-1 justify-center">
              <img
                class="h-8 w-auto"
                src="https://s3-eu-central-1.ionoscloud.com/jupus/images/logo.svg"
                alt="Jupus Logo"
              />
            </div>

            <!-- Right side placeholder (for balance) -->
            <div class="w-10"></div>
          </div>
        </div>

        <main class="flex-1 overflow-auto pt-14 transition-all duration-300 ease-in-out md:pt-0">
          <router-view />
        </main>
      </div>
      <router-view v-else />
    </div>

    <client404 v-else-if="!isExternalRoute && !isPreviewRoute && !isPdfFormSubmissionRoute" />
  </div>

  <div
    v-else-if="!isDataLoaded"
    class="flex h-screen w-full items-center justify-center"
  >
    <LoaderCircle
      data-cy="ClientPortalBaseLoadingSpinner"
      class="animate-spin"
      :size="100"
    />
  </div>

  <div
    v-else
    class="flex h-screen w-full items-center justify-center"
  >
    <client404 />
  </div>
</template>
